<app-header [forceLightBackground]="true"></app-header>
<div class="contribution-setup">
  <div class="contribution-setup--layout">
    <ng-content></ng-content>
    <div class="contribution-setup--content">
      <app-page-header></app-page-header>
      <div class="contribution-container">
        <!-- Step 1 Template -->
        <ng-template #step1Template>
          <form [formGroup]="contributionForm">
            <!-- Step 1: Setup -->
            <div class="step-content step-1">
              <header class="setup-step-header">
                <span class="setup-step-badge">Step 1</span>
                <h2 class="step-title">{{ config.setupTitle }}</h2>
                <p class="setup-step-subtitle">
                  Name your {{ config.type === 'model' ? 'model' : 'dataset' }} and choose how you want to add metadata to continue.
                </p>
              </header>

              <div class="setup-step-panel">
                <section class="setup-section setup-section--basic">
                  <div class="setup-section__head">
                    <div class="setup-section__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 class="setup-section__title">Basic details</h3>
                      <p class="setup-section__desc">
                        Choose a clear, unique title — it will appear in search and catalogue listings.
                      </p>
                    </div>
                  </div>

                  <div class="form-fields">
                    <div class="form-row">
                      <!-- Title Field -->
                      <div class="form-field" [class.col-6]="config.type !== 'dataset'" [class.col-12]="config.type === 'dataset'">
                        <label for="title" class="form-label">
                          Title <span class="required">*</span>
                        </label>
                        <div class="form-input-wrap">
                          <input
                            type="text"
                            id="title"
                            formControlName="title"
                            class="form-input"
                            [placeholder]="config.titlePlaceholder"
                            [class.error]="(contributionForm.get('title')?.invalid && contributionForm.get('title')?.touched) || titleAvailabilityStatus === 'not-available'"
                          />
                        </div>
                        <div
                          *ngIf="contributionForm.get('title')?.invalid && contributionForm.get('title')?.touched"
                          class="error-message"
                        >
                          Title is required
                        </div>
                        <!-- Title Availability Status -->
                        <div *ngIf="(config.type === 'dataset' || config.type === 'model') && contributionForm.get('title')?.value" class="title-availability-message">
                          <div *ngIf="isCheckingTitle" class="availability-checking">
                            <span class="availability-checking__dot" aria-hidden="true"></span>
                            Checking availability...
                          </div>
                          <div *ngIf="!isCheckingTitle && titleAvailabilityStatus === 'available'" class="availability-available">
                            <svg class="availability-icon availability-icon-tick" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="9" cy="9" r="8" fill="#4caf50" stroke="#4caf50" stroke-width="1"/>
                              <path d="M6 9L8 11L12 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            Title is available
                          </div>
                          <div *ngIf="!isCheckingTitle && titleAvailabilityStatus === 'not-available'" class="availability-not-available">
                            <svg class="availability-icon availability-icon-cross" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="9" cy="9" r="8" fill="#d32f2f" stroke="#d32f2f" stroke-width="1"/>
                              <path d="M6 6L12 12M12 6L6 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            Title is not available
                          </div>
                        </div>
                      </div>

                      <!-- Permission Dropdown with Checkboxes (models only) -->
                      <div *ngIf="config.type !== 'dataset'" class="form-field col-6">
                    <label for="permission" class="form-label">
                      Permission <span class="required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div
                        class="custom-dropdown-trigger"
                        [class.error]="contributionForm.get('permission')?.invalid && contributionForm.get('permission')?.touched"
                        [class.open]="isPermissionDropdownOpen"
                        (click)="togglePermissionDropdown()"
                      >
                        <div class="trigger-content">
                          <span
                            *ngIf="!contributionForm.get('permission')?.value"
                            class="placeholder"
                          >
                            Select permission
                          </span>
                          <span
                            *ngIf="contributionForm.get('permission')?.value"
                            class="permission-chip"
                            [class.permission-chip--public]="contributionForm.get('permission')?.value === 'Open'"
                            [class.permission-chip--restricted]="contributionForm.get('permission')?.value === 'Restricted'"
                          >
                            <img src="/public.svg" alt="" class="permission-chip__icon" aria-hidden="true" />
                            {{ getSelectedPermissionLabel() }}
                            <svg
                              class="chip-close-icon"
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              (click)="clearPermission($event)"
                            >
                              <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </span>
                        </div>
                        <svg
                          class="dropdown-arrow"
                          [class.rotated]="isPermissionDropdownOpen"
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                      <div
                        *ngIf="isPermissionDropdownOpen"
                        class="custom-dropdown-panel"
                        (click)="$event.stopPropagation()"
                      >
                        <div
                          *ngFor="let option of permissionOptions"
                          class="dropdown-option"
                          (click)="selectPermission(option)"
                        >
                          <div class="checkbox-wrapper">
                            <input
                              type="checkbox"
                              [checked]="isPermissionSelected(option.value)"
                              class="checkbox-custom"
                              readonly
                            />
                          </div>
                          <span class="option-label">{{ option.label }}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      *ngIf="contributionForm.get('permission')?.invalid && contributionForm.get('permission')?.touched"
                      class="error-message"
                    >
                      Permission is required
                    </div>
                  </div>
                    </div>
                  </div>
                </section>

                <!-- Problem Statement (datasets only) -->
                <section *ngIf="config.type === 'dataset'" class="setup-section setup-section--problem">
                  <div class="setup-section__head">
                    <div class="setup-section__icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                    <div>
                      <h3 class="setup-section__title">Problem statement</h3>
                      <p class="setup-section__desc">Choose the type of problem this dataset is intended to solve.</p>
                    </div>
                  </div>

                  <div class="form-fields">
                    <div class="form-field col-12">
                      <label for="problemStatement" class="form-label">
                        Problem statement <span class="required">*</span>
                      </label>
                      <div class="form-input-wrap">
                        <div class="select-dropdown" [class.select-dropdown--open]="isProblemStatementDropdownOpen">
                          <button
                            type="button"
                            id="problemStatement"
                            class="form-input select-dropdown__trigger"
                            [class.error]="contributionForm.get('problemStatement')?.invalid && contributionForm.get('problemStatement')?.touched"
                            [class.select-dropdown__trigger--placeholder]="!contributionForm.get('problemStatement')?.value"
                            [disabled]="contributionForm.get('problemStatement')?.disabled"
                            role="combobox"
                            aria-haspopup="listbox"
                            aria-controls="problemStatementListbox"
                            [attr.aria-expanded]="isProblemStatementDropdownOpen"
                            [attr.aria-activedescendant]="isProblemStatementDropdownOpen && problemStatementActiveIndex >= 0 ? 'problemStatementOption-' + problemStatementActiveIndex : null"
                            (click)="toggleProblemStatementDropdown()"
                            (keydown)="onProblemStatementKeydown($event)"
                          >
                            <span class="select-dropdown__value">{{ getSelectedProblemStatementLabel() }}</span>
                            <svg
                              class="select-dropdown__arrow"
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </button>
                          <ul
                            *ngIf="isProblemStatementDropdownOpen"
                            id="problemStatementListbox"
                            class="select-dropdown__panel"
                            role="listbox"
                            aria-label="Problem statement"
                          >
                            <li
                              *ngFor="let option of problemStatementOptions; let i = index"
                              [id]="'problemStatementOption-' + i"
                              class="select-dropdown__option"
                              [class.select-dropdown__option--active]="i === problemStatementActiveIndex"
                              [class.select-dropdown__option--selected]="option.value === contributionForm.get('problemStatement')?.value"
                              role="option"
                              [attr.aria-selected]="option.value === contributionForm.get('problemStatement')?.value"
                              (mouseenter)="problemStatementActiveIndex = i"
                              (click)="selectProblemStatement(option)"
                            >
                              <span class="select-dropdown__option-label">{{ option.label }}</span>
                              <svg
                                *ngIf="option.value === contributionForm.get('problemStatement')?.value"
                                class="select-dropdown__check"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                aria-hidden="true"
                              >
                                <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              </svg>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div
                        *ngIf="contributionForm.get('problemStatement')?.invalid && contributionForm.get('problemStatement')?.touched"
                        class="error-message"
                      >
                        Problem statement is required
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Metadata Option Selection -->
                <section class="setup-section setup-section--metadata">
                  <div class="setup-section__head">
                    <div class="setup-section__icon setup-section__icon--metadata" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                    </div>
                    <div>
                      <h3 class="setup-section__title">Metadata method</h3>
                      <p class="setup-section__desc">Select how you want to describe your {{ config.type === 'model' ? 'model' : 'dataset' }} in the next step.</p>
                    </div>
                  </div>

                  <div class="metadata-options">
                    <div class="metadata-options__grid">
                      <article
                        class="metadata-option-card"
                        [class.active]="contributionForm.get('metadataOption')?.value === 'enter'"
                        [class.disabled]="isUpdateMode || !canEnableMetadataButtons()"
                        role="button"
                        tabindex="0"
                        [attr.aria-disabled]="isUpdateMode || !canEnableMetadataButtons()"
                        (click)="!isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('enter')"
                        (keydown.enter)="!isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('enter')"
                        (keydown.space)="$event.preventDefault(); !isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('enter')"
                      >
                        <span
                          *ngIf="contributionForm.get('metadataOption')?.value === 'enter'"
                          class="metadata-option-card__selected-badge"
                          aria-hidden="true"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </span>
                        <div class="metadata-option-card__header">
                          <div class="metadata-option-card__icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </div>
                          <div class="metadata-option-card__titles">
                            <h4 class="metadata-option-card__title">{{ metadataOptionCopy.enter.title }}</h4>
                            <span class="metadata-option-card__tag">{{ metadataOptionCopy.enter.tag }}</span>
                          </div>
                        </div>
                        <div class="metadata-option-card__content">
                          <p class="metadata-option-card__description">
                            {{ metadataOptionCopy.enter.description }}
                          </p>
                        </div>
                        <div class="metadata-option-card__footer">
                          <button
                            type="button"
                            class="metadata-option-card__button"
                            [disabled]="isUpdateMode || !canEnableMetadataButtons()"
                            (click)="$event.stopPropagation(); selectMetadataOption('enter')"
                            [attr.aria-label]="metadataOptionCopy.enter.ariaLabel"
                          >
                            {{ metadataOptionCopy.enter.button }}
                          </button>
                        </div>
                      </article>

                      <div class="metadata-options__divider" aria-hidden="true">
                        <span class="metadata-options__divider-text">or</span>
                      </div>

                      <article
                        class="metadata-option-card"
                        [class.active]="contributionForm.get('metadataOption')?.value === 'upload'"
                        [class.disabled]="isUpdateMode || !canEnableMetadataButtons()"
                        role="button"
                        tabindex="0"
                        [attr.aria-disabled]="isUpdateMode || !canEnableMetadataButtons()"
                        (click)="!isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('upload')"
                        (keydown.enter)="!isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('upload')"
                        (keydown.space)="$event.preventDefault(); !isUpdateMode && canEnableMetadataButtons() && selectMetadataOption('upload')"
                      >
                        <span
                          *ngIf="contributionForm.get('metadataOption')?.value === 'upload'"
                          class="metadata-option-card__selected-badge"
                          aria-hidden="true"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </span>
                        <div class="metadata-option-card__header">
                          <div class="metadata-option-card__icon metadata-option-card__icon--upload">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M7 14L12 9L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M12 9V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </div>
                          <div class="metadata-option-card__titles">
                            <h4 class="metadata-option-card__title">{{ metadataOptionCopy.upload.title }}</h4>
                            <span class="metadata-option-card__tag metadata-option-card__tag--secondary">{{ metadataOptionCopy.upload.tag }}</span>
                          </div>
                        </div>
                        <div class="metadata-option-card__content">
                          <p class="metadata-option-card__description">
                            {{ metadataOptionCopy.upload.description }}
                          </p>
                        </div>
                        <div class="metadata-option-card__footer">
                          <button
                            type="button"
                            class="metadata-option-card__button metadata-option-card__button--secondary"
                            [disabled]="isUpdateMode || !canEnableMetadataButtons()"
                            (click)="$event.stopPropagation(); selectMetadataOption('upload')"
                            [attr.aria-label]="metadataOptionCopy.upload.ariaLabel"
                          >
                            {{ metadataOptionCopy.upload.button }}
                          </button>
                        </div>
                      </article>
                    </div>

                    <p *ngIf="contributionForm.get('title')?.value && !canEnableMetadataButtons()" class="metadata-options__hint">
                      Enter a unique, available title above to unlock metadata options.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </form>
        </ng-template>

        <!-- Stepper Component -->
        <app-stepper
          [steps]="stepperSteps"
          [currentStep]="currentStep"
          [hideNextOnFirstStep]="true"
          (stepChange)="onStepChange($event)"
          (next)="onNext()"
          (previous)="onPrevious()"
        ></app-stepper>
      </div>
    </div>
  </div>
</div>
